import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { BdsTableCell, BdsTableRow, BdsTypo } from 'blip-ds/dist/blip-ds-react';
import { trackEvent } from '../../../../api/Billing/Tracking';
import { getMonthTwoDigits } from '../../../../utils/date';
import { PageHeaderComponent } from '../../components/Metrics/PageHeader/PageHeaderComponent';
import { MiuConsumptionPaper } from '../styles';
import MiuConsumptionContext from '../../../../contexts/MiuConsumptionContext';
import { origin, REPORT_PERIOD_SELECT } from '../../../../shared/SegmentEvents';
import { billingRoutes } from '../../../../routes/BillingRoutes';
import { BillingMetrics } from '../../../../shared/BillingMetrics';
import FeatureToggleService from '../../../../services/FeatureToggleService';
import TableWithPagination from '../../../../components/TableWithPagination/TableWithPagination';
import { useSortedPaginatedData } from '../../../../hooks/useSortedPaginatedData';
import { DEFAULT_PAGE_OPTIONS, COLUMN_WIDTHS_3 as COLUMN_WIDTHS } from '../../../../utils/paginationUtils';

const COLUMN_KEYS = ['botIdentity', 'totalMiu', 'totalMsg'];

const parseMiuConsumption = (obj) => ({
  ...obj,
  totalMiu: obj.metricConsumption.find((item) => item.metricId !== BillingMetrics.exchangedMessage)?.total ?? 0,
  totalMsg: obj.metricConsumption.find((item) => item.metricId === BillingMetrics.exchangedMessage)?.total ?? 0,
});

export interface MiuConsumptionMonthlyUsageProps {
  isDetailedView?: boolean;
}

const MiuConsumptionMonthlyUsageComponent: FC<MiuConsumptionMonthlyUsageProps> = ({ isDetailedView = false }) => {
  const {
    dateToSearch,
    periodOptions,
    content,
    tenant,
    loggedUser,
    setHeaderContent,
    trackingProps,
    getPlanStatus,
    setDateToSearch,
    billingMiuConsumptionApi,
    miuMetricIds,
  } = useContext(MiuConsumptionContext);
  const [isLoading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);

  const { itemsPerPage, sortConfig, sortedData, paginatedData, handlePaginationChange, handleItemsPerPageChange, handleSortChange } =
    useSortedPaginatedData({ data });

  useEffect(() => {
    setHeaderContent({
      redirect: billingRoutes.dataConsumption,
      text: content.formatString(content.header, tenant.info.name),
    });
    return () => setHeaderContent({ redirect: null });
  }, []);

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        const storageType = (await FeatureToggleService.isDatabricksStorageTypeEnabled(BillingMetrics.monthlyActiveUser)) ? 1 : 0;

        const monthString = `${dateToSearch.getFullYear()}-${getMonthTwoDigits(dateToSearch)}`;
        const miuMetricIdsString = miuMetricIds.join(';');

        const rawData = await billingMiuConsumptionApi.getMonthlyMiuConsumption(
          tenant.info.id,
          miuMetricIdsString,
          monthString,
          -1,
          storageType,
        );

        setData(rawData.map(parseMiuConsumption));
      } finally {
        setLoading(false);
      }
    };

    getData();
  }, [dateToSearch]);

  const periodOptionChangeEvent = async (selectedOption) => {
    const dateToRetrieveData = new Date(selectedOption.detail.value);
    setDateToSearch(dateToRetrieveData);
    const planStatus = await getPlanStatus();
    trackEvent(REPORT_PERIOD_SELECT, tenant, loggedUser, {
      ...trackingProps,
      origin: origin.MIUPERCHATBOT,
      date: dateToRetrieveData,
      planStatus: planStatus,
    });
  };

  const tableRows = useMemo(
    () =>
      paginatedData.length > 0
        ? paginatedData.map((item) => (
            <BdsTableRow key={item.botIdentity}>
              <BdsTableCell>
                <BdsTypo variant="fs-14" bold="bold" margin={false}>
                  {item.botIdentity}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.totalMiu.toLocaleString(content.locale)}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.totalMsg.toLocaleString(content.locale)}
                </BdsTypo>
              </BdsTableCell>
            </BdsTableRow>
          ))
        : undefined,
    [paginatedData, content.locale],
  );

  const headings = [content.monthly.columnChatBot, content.monthly.columnTotalMiu, content.monthly.columnTotalMsg];

  return (
    <>
      {isDetailedView && (
        <PageHeaderComponent
          title={content.monthly.title}
          dateToSearch={dateToSearch}
          periodOptions={periodOptions}
          periodOptionChangeEvent={periodOptionChangeEvent}
        />
      )}
      <MiuConsumptionPaper>
        {!isDetailedView && (
          <BdsTypo variant="fs-24" bold="bold" margin={false}>
            {content.monthly.title}
          </BdsTypo>
        )}
        <BdsTypo variant="fs-14" margin={false}>{content.monthly.subtitle}</BdsTypo>
        <TableWithPagination
          options={{
            heading: headings,
            columnKeys: COLUMN_KEYS,
            columnWidths: COLUMN_WIDTHS,
            sortConfig,
            onSortChange: handleSortChange,
            itemsPerPageList: DEFAULT_PAGE_OPTIONS,
            itemsPerPage,
            numberOfItems: isLoading ? itemsPerPage : sortedData.length,
            isLoading,
            hasEmptyState: !isLoading && sortedData.length === 0,
            emptyStateSubtitle: '',
            isAutoHeight: true,
          }}
          onPaginationChange={handlePaginationChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        >
          {tableRows}
        </TableWithPagination>
      </MiuConsumptionPaper>
    </>
  );
};

export default MiuConsumptionMonthlyUsageComponent;
